import { usePermissionStore } from '@/stores/permission'

function applyNodeMask(node: HTMLElement, permissionStore: ReturnType<typeof usePermissionStore>) {
  const code = node.getAttribute('data-perm-code')?.trim() ?? ''
  if (!code) return
  node.style.display = permissionStore.hasPerm(code) ? '' : 'none'
}

/** 对容器内带 data-perm-code 的节点按当前用户按钮权限显示/隐藏（无属性者不处理） */
export function applyDeclaredButtonPermissionMask(root: HTMLElement | null | undefined) {
  if (!root) return
  const permissionStore = usePermissionStore()
  const nodes = root.querySelectorAll<HTMLElement>('[data-perm-code]')
  nodes.forEach((node) => applyNodeMask(node, permissionStore))
}

/**
 * 监听异步渲染节点，确保后插入的 data-perm-code 元素也会应用权限掩码。
 * 使用 rAF 合并同一帧内的大量 DOM 变更，避免路由切换时 MutationObserver 同步扫树造成卡顿。
 */
export function watchDeclaredButtonPermissionMask(
  root: HTMLElement | null | undefined,
): () => void {
  if (!root) return () => {}

  const pending = new Set<HTMLElement>()
  let rafId = 0

  const flush = () => {
    rafId = 0
    if (pending.size === 0) return
    const permissionStore = usePermissionStore()
    for (const node of pending) {
      if (!node.isConnected) continue
      applyNodeMask(node, permissionStore)
    }
    pending.clear()
  }

  const schedule = () => {
    if (rafId) return
    rafId = requestAnimationFrame(flush)
  }

  const collectFromSubtree = (el: HTMLElement) => {
    if (el.hasAttribute('data-perm-code')) pending.add(el)
    el.querySelectorAll<HTMLElement>('[data-perm-code]').forEach((child) => pending.add(child))
  }

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
        if (mutation.target.hasAttribute('data-perm-code')) {
          pending.add(mutation.target)
        }
        continue
      }
      if (mutation.type !== 'childList') continue
      mutation.addedNodes.forEach((added) => {
        if (added instanceof HTMLElement) collectFromSubtree(added)
      })
    }
    if (pending.size > 0) schedule()
  })

  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-perm-code'],
  })

  return () => {
    if (rafId) cancelAnimationFrame(rafId)
    pending.clear()
    observer.disconnect()
  }
}
